/**
 * decorate — annotate `CompletionCandidate`s with version-status metadata.
 *
 * The ranker consumes `demote` to bump the candidate's tier offset; the
 * orchestrator wraps `badge` and `detailSuffix` into Monaco's
 * `label.description` and `detail` fields when translating to a Monaco
 * `CompletionItem`.
 *
 * Modes:
 *   - `'annotated'` (default): show every candidate but demote `unavailable`
 *     by 2 tiers, demote `deprecated` by 1 tier; surface badges + detail.
 *   - `'strict'`: drop `unavailable` candidates entirely; keep `deprecated`
 *     visible (strikethrough handled in the orchestrator).
 *   - `'off'`: pass through with no decoration. Caller can skip this module
 *     entirely; it's a no-op convenience for tests.
 */

import type { CompletionCandidate } from '@/core/engines';
import type { SqlEngine } from '@/features/sql-grammar/parseOptions';

import {
  classifyFeature,
  type CompatContext,
  type FeatureStatus,
} from './resolveCompat';
import {
  getVersionMetadata,
  type VersionMetadataEntry,
} from './versionMetadata';

export type VersionFilterMode = 'strict' | 'annotated' | 'off';

export type DecoratedCandidate = CompletionCandidate & {
  readonly versionStatus: FeatureStatus;
  readonly badge?: string;
  readonly detailSuffix?: string;
  readonly demote: 0 | 1 | 2;
};

export function decorateCandidates(
  candidates: readonly CompletionCandidate[],
  compat: CompatContext,
  mode: VersionFilterMode = 'annotated',
): DecoratedCandidate[] {
  if (mode === 'off') {
    return candidates.map((c) => passThrough(c));
  }
  const out: DecoratedCandidate[] = [];
  for (const c of candidates) {
    const decorated = decorateOne(c, compat);
    if (mode === 'strict' && decorated.versionStatus === 'unavailable') {
      continue;
    }
    out.push(decorated);
  }
  return out;
}

function decorateOne(
  c: CompletionCandidate,
  compat: CompatContext,
): DecoratedCandidate {
  // Only keyword / function / system-function candidates are catalog-eligible
  // — relations/columns aren't version-gated by name, and snippets are
  // resolver-controlled.
  if (!isCatalogKind(c.kind)) return passThrough(c);
  const entry = getVersionMetadata(compat.engineId, c.label);
  if (!entry) return passThrough(c);
  const status = classifyFeature(entry, compat);
  if (status === 'supported') return passThrough(c);
  return {
    ...c,
    versionStatus: status,
    badge: badgeFor(entry, status),
    detailSuffix: detailFor(entry, status, compat),
    demote: status === 'unavailable' ? 2 : 1,
    deprecated: c.deprecated || status === 'deprecated',
  };
}

function passThrough(c: CompletionCandidate): DecoratedCandidate {
  return { ...c, versionStatus: 'supported', demote: 0 };
}

function isCatalogKind(kind: CompletionCandidate['kind']): boolean {
  return (
    kind === 'keyword' ||
    kind === 'system-function' ||
    kind === 'system-object' ||
    kind === 'routine'
  );
}

function badgeFor(
  entry: VersionMetadataEntry,
  status: FeatureStatus,
): string {
  if (entry.badge) return entry.badge;
  if (status === 'unavailable') {
    if (entry.addedIn !== undefined) {
      return `≥ ${formatVersionLabel(entry.addedIn)}`;
    }
    return 'unavailable';
  }
  if (status === 'deprecated') {
    if (entry.deprecatedAtCompat !== undefined) {
      return `deprecated@${entry.deprecatedAtCompat}`;
    }
    return 'deprecated';
  }
  return '';
}

function detailFor(
  entry: VersionMetadataEntry,
  status: FeatureStatus,
  compat: CompatContext,
): string {
  const engineLabel = engineDisplayName(entry.engineId);
  if (status === 'unavailable' && entry.addedIn) {
    const reported = compat.serverVersion ? ` — your server is ${compat.serverVersion}` : '';
    return `Requires ${engineLabel} ${formatVersionLabel(entry.addedIn)}+${reported}`;
  }
  if (status === 'deprecated') {
    if (entry.removedAtCompat !== undefined) {
      return `Removed at compat ${entry.removedAtCompat}`;
    }
    if (entry.deprecatedAtCompat !== undefined) {
      return `Deprecated at compat ${entry.deprecatedAtCompat}`;
    }
    return 'Deprecated';
  }
  return '';
}

function engineDisplayName(engineId: SqlEngine): string {
  switch (engineId) {
    case 'mssql':
      return 'SQL Server';
    case 'postgres':
      return 'PostgreSQL';
    case 'mysql':
      return 'MySQL';
    case 'oracle':
      return 'Oracle';
    case 'sqlite':
      return 'SQLite';
  }
}

function formatVersionLabel(v: string): string {
  // Engine-agnostic passthrough: badges/detail copy show the raw version
  // string as authored in the metadata catalog (MSSQL years like '2017',
  // semver-ish like '15' / '3.35' for the other engines).
  return v;
}
